import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ApiError } from '../Models/ApiModel';
import type { StudentDraftModel, StudentModel } from '../Models/StudentModel';
import { StudentService } from '../Services/StudentService';
import { CalendarAutoSync } from '../Services/CalendarAutoSync';
import type { RootState } from './store';

/**
 * The roster.
 *
 * This slice replaces the global `_studentCache` the JavaScript app kept, and
 * it exists for the same reason: the lesson list needs a student's recurring
 * slots to decide whether a lesson has ended, and refetching the roster for
 * every row is not an option.
 */

export type LoadState = 'idle' | 'loading' | 'ready' | 'failed';

export interface StudentsState {
  items: StudentModel[];
  state: LoadState;
  error: string | null;
}

const initialState: StudentsState = { items: [], state: 'idle', error: null };

function message(error: unknown): string {
  return error instanceof ApiError ? error.code : 'SERVER_ERROR';
}

export const loadStudents = createAsyncThunk('students/load', async () => StudentService.list());

export const saveStudent = createAsyncThunk(
  'students/save',
  async (input: { name?: string; draft: StudentDraftModel }) => {
    if (!input.name) return StudentService.create(input.draft);
    return StudentService.update(input.name, input.draft);
  }
);

/**
 * The API cascades a student's lessons and recurring slots away with them.
 * Their calendar events would not go anywhere on their own, so the ids are
 * collected first and the events removed once the delete has succeeded.
 */
export const removeStudent = createAsyncThunk('students/remove', async (name: string, { getState }) => {
  const state = getState() as RootState;
  const lessons = state.lessons.items.filter(lesson => lesson.student === name);
  const slots = state.students.items.find(student => student.name === name)?.slots ?? [];
  await StudentService.remove(name);
  void CalendarAutoSync.lessonsRemoved(lessons);
  for (const slot of slots) void CalendarAutoSync.slotRemoved(slot);
  return name;
});

const studentsSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    /** Applied after a change elsewhere, so no full refetch is needed. */
    upsert(state, action: PayloadAction<StudentModel>) {
      const index = state.items.findIndex(student => student.name === action.payload.name);
      if (index === -1) state.items.push(action.payload);
      else state.items[index] = action.payload;
      state.items.sort((a, b) => a.name.localeCompare(b.name));
    }
  },
  extraReducers: builder => {
    builder
      .addCase(loadStudents.pending, state => {
        state.state = 'loading';
        state.error = null;
      })
      .addCase(loadStudents.fulfilled, (state, action) => {
        state.items = action.payload;
        state.state = 'ready';
      })
      .addCase(loadStudents.rejected, (state, action) => {
        state.state = 'failed';
        state.error = message(action.error);
      })
      .addCase(saveStudent.fulfilled, (state, action) => {
        const saved = action.payload;
        // A rename comes back with the name it used to have. Dropping the old
        // entry here is what keeps the list from showing the student twice.
        const previous = (saved as { previousName?: string }).previousName;
        if (previous) {
          state.items = state.items.filter(student => student.name !== previous);
        }
        const index = state.items.findIndex(student => student.name === saved.name);
        // The API answers a save with the student alone; the relations it was
        // holding are unchanged, so they are carried across rather than lost.
        const existing = index === -1 ? undefined : state.items[index];
        const merged: StudentModel = {
          ...saved,
          slots: existing?.slots ?? saved.slots ?? [],
          contacts: existing?.contacts ?? saved.contacts ?? [],
          availability: existing?.availability ?? saved.availability ?? [],
          absences: existing?.absences ?? saved.absences ?? []
        };
        if (index === -1) state.items.push(merged);
        else state.items[index] = merged;
        state.items.sort((a, b) => a.name.localeCompare(b.name));
      })
      .addCase(removeStudent.fulfilled, (state, action) => {
        state.items = state.items.filter(student => student.name !== action.payload);
      });
  }
});

export const { upsert } = studentsSlice.actions;
export default studentsSlice.reducer;
